import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listGbpActions, markGbpAction,
  type GbpActionsClientDto, type GbpActionDto, type GbpActionStatus,
} from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  buildJson, buildMarkdown, clientLabel, copyText, downloadText, fmtDate, isSettled, visibleActions,
  type ExportOptions,
} from '../lib/gbp-export';
import { GBIBadge, GBIBtn, GBICard, T, sharedStyles } from '../components/Shared';

// Cross-client GBP worklist.
//
// The per-client AI Insight panel answers "what do we change for this business".
// This answers it for the whole book at once, which is the thing a ~100-client
// sweep actually needs: one page, one selection, one export.
//
// Two overviews, matching the report's own tabs minus reviews:
//   Data & Analysis — why each profile stands where it does
//   Actions         — what to change inside the profile
// Reviews are deliberately absent; they are their own tab and their own routine.
//
// Selection drives EXPORT ONLY. The list always shows everything matching the
// filters, so ticking a client never hides work from view.

type View = 'actions' | 'analysis';
type GroupBy = 'client' | 'field';
type OwnerFilter = ExportOptions['owner'];
type StatusFilter = ExportOptions['status'];

export function AdminGbpActions({ embedded = false }: { embedded?: boolean }) {
  const state = useAsync(listGbpActions, []);
  const [view, setView] = useState<View>('actions');
  const [groupBy, setGroupBy] = useState<GroupBy>('client');
  const [owner, setOwner] = useState<OwnerFilter>('all');
  // Default to what is still outstanding — the usual question on a sweep.
  const [status, setStatus] = useState<StatusFilter>('remaining');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeAnalysis, setIncludeAnalysis] = useState(true);
  const [includeActions, setIncludeActions] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);

  const raw = state.status === 'success' ? state.data : [];

  const flash = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  }, []);

  // Marks are applied over the fetched data rather than by refetching: ticking
  // off 40 actions in a row should not mean 40 round trips through the whole
  // book. Keyed clientId:actionKey; the value is the authoritative progress the
  // server echoed back (or null once cleared).
  const [marks, setMarks] = useState<Map<string, GbpActionDto['progress']>>(new Map());

  const all = useMemo(() => {
    if (marks.size === 0) return raw;
    return raw.map(c => ({
      ...c,
      actions: c.actions.map(a => {
        const k = `${c.clientId}:${a.actionKey}`;
        return marks.has(k) ? { ...a, progress: marks.get(k) ?? null } : a;
      }),
    }));
  }, [raw, marks]);

  const mark = useCallback(async (
    clientId: string, action: GbpActionDto, next: GbpActionStatus | null,
  ) => {
    const k = `${clientId}:${action.actionKey}`;
    const previous = marks.get(k);
    const had = marks.has(k);
    // Optimistic: the row responds instantly, then reconciles with the server.
    setMarks(m => new Map(m).set(k, next
      ? { status: next, markedAt: Date.now(), markedBy: null, changedSinceMarked: false }
      : null));
    try {
      const saved = await markGbpAction(clientId, action, next);
      setMarks(m => new Map(m).set(k, saved));
    } catch (e) {
      // Roll back to exactly what was there before, so a failed save cannot
      // leave the row showing work as done that was never recorded.
      setMarks(m => {
        const copy = new Map(m);
        if (had) copy.set(k, previous ?? null); else copy.delete(k);
        return copy;
      });
      flash(`Could not save: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }, [marks, flash]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(c => {
      if (!q) return true;
      // Search the client identity AND the action text — "description" or
      // "hours" should surface every client carrying that kind of work.
      const hay = [
        c.name, c.location, c.locationLabel ?? '', c.groupLabel ?? '', c.category ?? '',
        ...c.actions.flatMap(a => [a.title, a.field, a.target, a.current]),
        ...(c.analysis?.gaps ?? []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [all, search]);

  const stats = useMemo(() => {
    const withReport = all.filter(c => c.hasReport);
    const actions = withReport.flatMap(c => c.actions);
    const settled = actions.filter(isSettled);
    const remaining = actions.filter(a => !isSettled(a));
    return {
      clients: all.length,
      withReport: withReport.length,
      missing: all.length - withReport.length,
      actions: actions.length,
      remaining: remaining.length,
      done: settled.filter(a => a.progress?.status === 'done').length,
      skipped: settled.filter(a => a.progress?.status === 'skipped').length,
      auto: remaining.filter(a => a.owner === 'auto').length,
      approve: remaining.filter(a => a.owner === 'approve').length,
    };
  }, [all]);

  const selectedClients = useMemo(
    () => all.filter(c => selected.has(c.clientId)),
    [all, selected],
  );

  const toggle = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleExpand = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const exportOpts: ExportOptions = { includeAnalysis, includeActions, owner, status };
  const nothingToInclude = !includeAnalysis && !includeActions;

  const doCopy = async () => {
    const ok = await copyText(buildMarkdown(selectedClients, exportOpts));
    flash(ok ? `Copied ${selectedClients.length} client(s) to clipboard` : 'Copy failed — use Download instead');
  };
  const stampName = (ext: string) =>
    `gbp-worklist-${selectedClients.length}-clients-${new Date().toISOString().slice(0, 10)}.${ext}`;
  const doDownloadMd = () => {
    downloadText(stampName('md'), buildMarkdown(selectedClients, exportOpts), 'text/markdown');
    flash('Markdown downloaded');
  };
  const doDownloadJson = () => {
    downloadText(stampName('json'), buildJson(selectedClients, exportOpts), 'application/json');
    flash('JSON downloaded');
  };

  return (
    <div style={{
      height: embedded ? 'auto' : '100vh',
      overflowY: embedded ? 'visible' : 'auto',
      background: T.bgSurface,
      padding: embedded ? 0 : '40px 32px',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', paddingBottom: selected.size > 0 ? 96 : 24 }}>
        {!embedded && <>
          <Link to="/admin" style={{ fontSize: 12, color: T.fgSecondary, textDecoration: 'none' }}>← Back to clients</Link>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary, marginTop: 8, marginBottom: 4 }}>
            GBP Action Worklist
          </div>
        </>}
        <div style={{ fontSize: 13, color: T.fgSecondary, marginBottom: 18, lineHeight: 1.5 }}>
          Every client's stored GBP analysis and action plan in one place, so a full sweep is one page instead of one report per client.
          Tick clients to build an export — the list itself always shows everything matching the filters.
          Reviews are tracked in their own report tab and are not included here.
        </div>

        {state.status === 'loading' && <div style={{ color: T.fgMuted, fontSize: 13, padding: 16 }}>Loading…</div>}
        {state.status === 'error' && (
          <GBICard style={{ borderLeft: `3px solid ${T.magenta}` }}>
            <div style={{ fontSize: 13, color: T.fgPrimary }}>Failed to load the worklist: {state.error.message}</div>
          </GBICard>
        )}

        {state.status === 'success' && (
          <>
            <StatRow stats={stats} />

            <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, marginBottom: 16, marginTop: 20 }}>
              <Tab active={view === 'actions'} onClick={() => setView('actions')}>
                Actions <Count>{stats.actions}</Count>
              </Tab>
              <Tab active={view === 'analysis'} onClick={() => setView('analysis')}>
                Data &amp; Analysis <Count>{stats.withReport}</Count>
              </Tab>
            </div>

            <Toolbar
              view={view}
              groupBy={groupBy} setGroupBy={setGroupBy}
              owner={owner} setOwner={setOwner}
              status={status} setStatus={setStatus}
              search={search} setSearch={setSearch}
              visible={filtered}
              selected={selected} setSelected={setSelected}
            />

            {filtered.length === 0 && (
              <div style={{ color: T.fgMuted, fontSize: 13, padding: 24, textAlign: 'center' }}>
                No client matches “{search}”.
              </div>
            )}

            {view === 'actions' && groupBy === 'client' && filtered.map(c => (
              <ClientActionsCard
                key={c.clientId} client={c} owner={owner} status={status}
                checked={selected.has(c.clientId)} onToggle={() => toggle(c.clientId)}
                expanded={expanded.has(c.clientId)} onExpand={() => toggleExpand(c.clientId)}
                onCopied={flash} onMark={mark}
              />
            ))}

            {view === 'actions' && groupBy === 'field' && (
              <FieldGroups
                clients={filtered} owner={owner} status={status}
                selected={selected} onToggle={toggle} onCopied={flash} onMark={mark}
              />
            )}

            {view === 'analysis' && filtered.map(c => (
              <ClientAnalysisCard
                key={c.clientId} client={c}
                checked={selected.has(c.clientId)} onToggle={() => toggle(c.clientId)}
                expanded={expanded.has(c.clientId)} onExpand={() => toggleExpand(c.clientId)}
              />
            ))}
          </>
        )}
      </div>

      {selected.size > 0 && (
        <ExportBar
          count={selected.size}
          statusNote={status === 'remaining' ? 'outstanding only'
            : status === 'done' ? 'done/skipped only' : 'any status'}
          includeAnalysis={includeAnalysis} setIncludeAnalysis={setIncludeAnalysis}
          includeActions={includeActions} setIncludeActions={setIncludeActions}
          disabled={nothingToInclude}
          onCopy={doCopy} onMd={doDownloadMd} onJson={doDownloadJson}
          onClear={() => setSelected(new Set())}
        />
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: selected.size > 0 ? 96 : 24, left: '50%', transform: 'translateX(-50%)',
          background: T.fgPrimary, color: '#fff', fontSize: 12, fontWeight: 600,
          padding: '9px 16px', borderRadius: 6, zIndex: 60, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
        }}>{toast}</div>
      )}
    </div>
  );
}

// ── Header stats ────────────────────────────────────────────────────────────

interface Stats {
  clients: number; withReport: number; missing: number; actions: number;
  remaining: number; done: number; skipped: number; auto: number; approve: number;
}

function StatRow({ stats }: { stats: Stats }) {
  const items: { label: string; value: number; color?: string; hint?: string }[] = [
    { label: 'Clients', value: stats.clients },
    { label: 'No report yet', value: stats.missing, color: stats.missing > 0 ? T.amber : T.fgMuted, hint: 'Coverage hole — generate before sweeping' },
    { label: 'Remaining', value: stats.remaining, color: T.fgPrimary, hint: `of ${stats.actions} actions total` },
    { label: 'Auto (safe)', value: stats.auto, color: T.cyanDim, hint: 'Outstanding, apply directly' },
    { label: 'Needs approval', value: stats.approve, color: T.magenta, hint: 'Outstanding, can trigger re-verification' },
    { label: 'Done', value: stats.done, color: T.green, hint: 'Applied inside the profile' },
    { label: 'Skipped', value: stats.skipped, color: T.fgMuted, hint: 'Deliberately not doing' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
      {items.map(i => (
        <div key={i.label} title={i.hint} style={{
          background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.fgMuted }}>
            {i.label}
          </div>
          <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 24, fontWeight: 700, color: i.color ?? T.fgPrimary, marginTop: 2 }}>
            {i.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────────────

function Toolbar({ view, groupBy, setGroupBy, owner, setOwner, status, setStatus, search, setSearch, visible, selected, setSelected }: {
  view: View;
  groupBy: GroupBy; setGroupBy: (g: GroupBy) => void;
  owner: OwnerFilter; setOwner: (o: OwnerFilter) => void;
  status: StatusFilter; setStatus: (s: StatusFilter) => void;
  search: string; setSearch: (s: string) => void;
  visible: GbpActionsClientDto[];
  selected: Set<string>; setSelected: (s: Set<string>) => void;
}) {
  const selectAllVisible = () => setSelected(new Set(visible.map(c => c.clientId)));
  const selectWithReport = () => setSelected(new Set(visible.filter(c => c.hasReport).map(c => c.clientId)));
  const selectNeedsApproval = () =>
    setSelected(new Set(visible.filter(c => c.actions.some(a => a.owner === 'approve' && !isSettled(a))).map(c => c.clientId)));

  const allVisibleSelected = visible.length > 0 && visible.every(c => selected.has(c.clientId));

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
      background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8,
      padding: '10px 12px', marginBottom: 16,
    }}>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search client, field or action…"
        style={{
          flex: '1 1 220px', minWidth: 180, fontSize: 13, padding: '7px 10px',
          border: `1px solid ${T.border}`, borderRadius: 6, outline: 'none',
          fontFamily: sharedStyles.fontBody, color: T.fgPrimary,
        }}
      />

      {view === 'actions' && (
        <Seg
          value={groupBy}
          onChange={v => setGroupBy(v as GroupBy)}
          options={[{ v: 'client', l: 'By client' }, { v: 'field', l: 'By GBP field' }]}
        />
      )}

      {view === 'actions' && (
        <Seg
          value={owner}
          onChange={v => setOwner(v as OwnerFilter)}
          options={[
            { v: 'all', l: 'All' },
            { v: 'auto', l: 'Auto' },
            { v: 'approve', l: 'Approval' },
          ]}
        />
      )}

      {view === 'actions' && (
        <Seg
          value={status}
          onChange={v => setStatus(v as StatusFilter)}
          options={[
            { v: 'remaining', l: 'Remaining' },
            { v: 'done', l: 'Done' },
            { v: 'all', l: 'Any' },
          ]}
        />
      )}

      <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
        <GBIBtn size="sm" variant="ghost" onClick={allVisibleSelected ? () => setSelected(new Set()) : selectAllVisible}>
          {allVisibleSelected ? 'Clear all' : `Select all (${visible.length})`}
        </GBIBtn>
        <GBIBtn size="sm" variant="ghost" onClick={selectWithReport}>With report</GBIBtn>
        {view === 'actions' && (
          <GBIBtn size="sm" variant="ghost" onClick={selectNeedsApproval}>Needs approval</GBIBtn>
        )}
      </div>
    </div>
  );
}

function Seg({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { v: string; l: string }[] }) {
  return (
    <div style={{ display: 'flex', border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}>
      {options.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)} style={{
          border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: value === o.v ? 700 : 500,
          padding: '7px 11px', fontFamily: sharedStyles.fontBody,
          background: value === o.v ? 'rgba(18,181,196,0.12)' : '#fff',
          color: value === o.v ? T.cyanDim : T.fgSecondary,
        }}>{o.l}</button>
      ))}
    </div>
  );
}

// ── Actions, grouped by client ──────────────────────────────────────────────

function ClientActionsCard({ client, owner, status, checked, onToggle, expanded, onExpand, onCopied, onMark }: {
  client: GbpActionsClientDto; owner: OwnerFilter; status: StatusFilter;
  checked: boolean; onToggle: () => void;
  expanded: boolean; onExpand: () => void;
  onCopied: (m: string) => void;
  onMark: MarkFn;
}) {
  const acts = visibleActions(client, owner, status);
  // Progress is always counted against the client's whole plan, never the
  // filtered slice — otherwise "3 of 3 done" would appear the moment a filter
  // hid the outstanding ones.
  const totalN = client.actions.length;
  const doneN = client.actions.filter(isSettled).length;

  return (
    <GBICard style={{ marginBottom: 10, padding: 0, borderLeft: checked ? `3px solid ${T.cyan}` : undefined }}>
      <CardHeader
        client={client} checked={checked} onToggle={onToggle}
        expanded={expanded} onExpand={onExpand}
        right={client.hasReport
          ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ProgressPill done={doneN} total={totalN} />
              <span style={{ fontSize: 11, color: T.fgSecondary }}>{acts.length} shown</span>
            </span>
          : <GBIBadge color={T.amber}>No report</GBIBadge>}
      />
      {expanded && (
        <div style={{ padding: '0 16px 14px 44px' }}>
          {!client.hasReport && (
            <div style={{ fontSize: 12, color: T.fgMuted, paddingTop: 4 }}>
              No GBP Optimization report has been generated for this client yet.
            </div>
          )}
          {client.hasReport && acts.length === 0 && (
            <div style={{ fontSize: 12, color: T.fgMuted, paddingTop: 4 }}>
              {totalN > 0 && doneN === totalN
                ? 'Every action for this client is done or skipped.'
                : 'No action matches the current filters.'}
            </div>
          )}
          {acts.map(a => (
            <ActionRow
              key={a.actionKey} action={a} onCopied={onCopied}
              onMark={next => onMark(client.clientId, a, next)}
            />
          ))}
        </div>
      )}
    </GBICard>
  );
}

type MarkFn = (clientId: string, action: GbpActionDto, next: GbpActionStatus | null) => void;

function ProgressPill({ done, total }: { done: number; total: number }) {
  if (total === 0) return null;
  const complete = done === total;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '2px 8px',
      background: complete ? 'rgba(24,128,56,0.12)' : T.bgOverlay,
      color: complete ? T.green : T.fgSecondary,
    }}>{done}/{total} done</span>
  );
}

function CardHeader({ client, checked, onToggle, expanded, onExpand, right }: {
  client: GbpActionsClientDto; checked: boolean; onToggle: () => void;
  expanded: boolean; onExpand: () => void; right: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
      <input
        type="checkbox" checked={checked} onChange={onToggle}
        aria-label={`Select ${clientLabel(client)} for export`}
        style={{ width: 16, height: 16, accentColor: T.cyan, cursor: 'pointer', flexShrink: 0 }}
      />
      <button onClick={onExpand} style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
        background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        fontFamily: sharedStyles.fontBody,
      }}>
        <span style={{ color: T.fgMuted, fontSize: 11, width: 10 }}>{expanded ? '▾' : '▸'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.fgPrimary }}>{clientLabel(client)}</span>
          <span style={{ fontSize: 12, color: T.fgMuted, marginLeft: 8 }}>{client.location}</span>
          <span style={{ display: 'block', fontSize: 11, color: T.fgMuted, marginTop: 2 }}>
            {client.score != null ? `Health ${client.score}/100` : 'No score'} · {fmtDate(client.generatedAt)}
          </span>
        </span>
        {right}
      </button>
    </div>
  );
}

function ActionRow({ action, prefix, onCopied, onMark }: {
  action: GbpActionDto; prefix?: string;
  onCopied: (m: string) => void;
  onMark: (next: GbpActionStatus | null) => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!action.pasteBody) return;
    const ok = await copyText(action.pasteBody);
    setCopied(ok);
    onCopied(ok ? 'Paste text copied' : 'Copy failed');
    window.setTimeout(() => setCopied(false), 1800);
  };

  const settled = isSettled(action);
  const stale = !!action.progress?.changedSinceMarked;

  return (
    <div style={{
      borderTop: `1px solid ${T.border}`, padding: '11px 0',
      // Settled work stays readable but visibly recedes, so the eye lands on
      // what is still outstanding when the filter is set to show everything.
      opacity: settled ? 0.55 : 1,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 13, fontWeight: 600, color: T.fgPrimary, flex: 1, minWidth: 200,
          textDecoration: settled ? 'line-through' : undefined,
        }}>
          {prefix && <span style={{ color: T.cyanDim, fontWeight: 700 }}>{prefix} · </span>}
          {action.title}
        </span>
        {action.owner === 'approve'
          ? <GBIBadge color={T.magenta}>Needs approval</GBIBadge>
          : <GBIBadge color={T.cyan}>Auto</GBIBadge>}
        {action.impact != null && (
          <span style={{ fontSize: 11, color: T.fgMuted }}>
            Impact {action.impact}{action.effort != null ? ` · Effort ${action.effort}` : ''}{action.risk != null ? ` · Risk ${action.risk}` : ''}
          </span>
        )}
        <MarkControls action={action} onMark={onMark} />
      </div>

      {stale && (
        <div style={{
          marginTop: 6, fontSize: 11, fontWeight: 600, color: T.amber,
          background: 'rgba(245,160,32,0.10)', border: `1px solid rgba(245,160,32,0.35)`,
          borderRadius: 6, padding: '6px 9px',
        }}>
          Marked {action.progress!.status} on {fmtDate(action.progress!.markedAt)}, but the recommendation has changed since — this is outstanding again.
        </div>
      )}

      {action.where && (
        <div style={{ fontSize: 11, color: T.cyanDim, fontWeight: 600, marginTop: 4, letterSpacing: '0.02em' }}>
          {action.where}
        </div>
      )}

      {(action.current || action.target) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 8, marginTop: 8 }}>
          {action.current && <Delta label="Current" text={action.current} tone="bad" />}
          {action.target && <Delta label="Target" text={action.target} tone="good" />}
        </div>
      )}

      {action.pasteBody && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.fgMuted }}>
              {action.pasteLabel ?? 'Ready to paste'}
            </span>
            <GBIBtn size="sm" variant={copied ? 'primary' : 'secondary'} onClick={copy} style={{ padding: '3px 9px', fontSize: 11 }}>
              {copied ? '✓ Copied' : 'Copy'}
            </GBIBtn>
          </div>
          <pre style={{
            margin: 0, background: T.bgOverlay, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: '9px 11px', fontSize: 12, lineHeight: 1.5, color: T.fgPrimary,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
          }}>{action.pasteBody}</pre>
        </div>
      )}

      {action.why && (
        <div style={{ fontSize: 12, color: T.fgSecondary, marginTop: 6, lineHeight: 1.5 }}>{action.why}</div>
      )}
    </div>
  );
}

function MarkControls({ action, onMark }: {
  action: GbpActionDto;
  onMark: (next: GbpActionStatus | null) => void;
}) {
  const s = action.progress?.status ?? null;
  // Clicking the active state again clears it — one control, no separate undo.
  const btn = (label: string, value: GbpActionStatus, activeColor: string) => {
    const active = s === value;
    return (
      <button
        onClick={() => onMark(active ? null : value)}
        title={active ? 'Click to clear this mark' : undefined}
        style={{
          border: `1px solid ${active ? activeColor : T.border}`,
          background: active ? activeColor : '#fff',
          color: active ? '#fff' : T.fgSecondary,
          borderRadius: 6, padding: '3px 9px', fontSize: 11, fontWeight: 700,
          cursor: 'pointer', fontFamily: sharedStyles.fontBody, whiteSpace: 'nowrap',
        }}
      >{active ? `✓ ${label}` : label}</button>
    );
  };
  return (
    <span style={{ display: 'flex', gap: 5 }}>
      {btn('Done', 'done', T.green)}
      {btn('Skip', 'skipped', T.fgMuted)}
    </span>
  );
}

function Delta({ label, text, tone }: { label: string; text: string; tone: 'good' | 'bad' }) {
  const bg = tone === 'good' ? 'rgba(24,128,56,0.07)' : 'rgba(232,80,26,0.06)';
  const bd = tone === 'good' ? 'rgba(24,128,56,0.25)' : 'rgba(232,80,26,0.25)';
  return (
    <div style={{ background: bg, border: `1px solid ${bd}`, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.fgMuted, marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: T.fgPrimary, lineHeight: 1.5 }}>{text}</div>
    </div>
  );
}

// ── Actions, grouped by GBP field ───────────────────────────────────────────
// The bulk-work view: every client needing a description change sits together,
// so one pass through the list is one field across the whole book.

function FieldGroups({ clients, owner, status, selected, onToggle, onCopied, onMark }: {
  clients: GbpActionsClientDto[]; owner: OwnerFilter; status: StatusFilter;
  selected: Set<string>; onToggle: (id: string) => void;
  onCopied: (m: string) => void;
  onMark: MarkFn;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, { client: GbpActionsClientDto; action: GbpActionDto }[]>();
    for (const c of clients) {
      for (const a of visibleActions(c, owner, status)) {
        const key = a.field || 'unspecified';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push({ client: c, action: a });
      }
    }
    // Biggest batches first — that is the order a bulk pass wants to work in.
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [clients, owner, status]);

  if (!groups.length) {
    return (
      <div style={{ color: T.fgMuted, fontSize: 13, padding: 24, textAlign: 'center' }}>
        {status === 'remaining'
          ? 'Nothing outstanding — every action matching this filter is done or skipped.'
          : 'No actions match the current filters.'}
      </div>
    );
  }

  return (
    <>
      {groups.map(([field, rows]) => (
        <GBICard key={field} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 15, fontWeight: 700, color: T.fgPrimary }}>
              {field}
            </span>
            <GBIBadge color={T.cyan}>{rows.length} client{rows.length === 1 ? '' : 's'}</GBIBadge>
            <GBIBtn
              size="sm" variant="ghost" style={{ marginLeft: 'auto' }}
              onClick={() => rows.forEach(r => { if (!selected.has(r.client.clientId)) onToggle(r.client.clientId); })}
            >Select these clients</GBIBtn>
          </div>
          {rows.map(r => (
            <ActionRow
              key={`${r.client.clientId}-${r.action.actionKey}`}
              action={r.action}
              prefix={clientLabel(r.client)}
              onCopied={onCopied}
              onMark={next => onMark(r.client.clientId, r.action, next)}
            />
          ))}
        </GBICard>
      ))}
    </>
  );
}

// ── Data & Analysis overview ────────────────────────────────────────────────

function ClientAnalysisCard({ client, checked, onToggle, expanded, onExpand }: {
  client: GbpActionsClientDto; checked: boolean; onToggle: () => void;
  expanded: boolean; onExpand: () => void;
}) {
  const a = client.analysis;
  const toneColor = (t: 'good' | 'warn' | 'bad') => t === 'good' ? T.green : t === 'bad' ? T.magenta : T.amber;

  return (
    <GBICard style={{ marginBottom: 10, padding: 0, borderLeft: checked ? `3px solid ${T.cyan}` : undefined }}>
      <CardHeader
        client={client} checked={checked} onToggle={onToggle}
        expanded={expanded} onExpand={onExpand}
        right={client.hasReport
          ? <span style={{ fontSize: 11, color: T.fgSecondary }}>{a?.gaps.length ?? 0} gap{(a?.gaps.length ?? 0) === 1 ? '' : 's'}</span>
          : <GBIBadge color={T.amber}>No report</GBIBadge>}
      />

      {!expanded && client.summary && (
        <div style={{ padding: '0 16px 12px 44px', fontSize: 12, color: T.fgSecondary, lineHeight: 1.55 }}>
          {client.summary}
        </div>
      )}

      {expanded && (
        <div style={{ padding: '0 16px 14px 44px' }}>
          {!a && <div style={{ fontSize: 12, color: T.fgMuted, paddingTop: 4 }}>No analysis stored for this client yet.</div>}
          {a && <>
            {a.executiveSummary && <Block title="Executive summary"><P>{a.executiveSummary}</P></Block>}
            {a.diagnosis && (
              <Block title="Diagnosis">
                {a.diagnosis.problem && <P><b>Problem — </b>{a.diagnosis.problem}</P>}
                {a.diagnosis.opportunity && <P><b>Opportunity — </b>{a.diagnosis.opportunity}</P>}
              </Block>
            )}
            {a.gaps.length > 0 && <Block title="Gaps"><Ul items={a.gaps} /></Block>}
            {a.profileInsight && (
              <Block title="Profile insight">
                <P style={{ borderLeft: `3px solid ${toneColor(a.profileInsight.tone)}`, paddingLeft: 10 }}>{a.profileInsight.text}</P>
              </Block>
            )}
            {a.mapsInsight && (
              <Block title="Map insight">
                <P style={{ borderLeft: `3px solid ${toneColor(a.mapsInsight.tone)}`, paddingLeft: 10 }}>{a.mapsInsight.text}</P>
              </Block>
            )}
            {a.aiInsight && <Block title="Answer-engine insight"><P>{a.aiInsight}</P></Block>}
            {a.competitorInsights.length > 0 && <Block title="Competitor insights"><Ul items={a.competitorInsights} /></Block>}
            {a.vulnerableCompetitors.length > 0 && (
              <Block title="Vulnerable competitors">
                {a.vulnerableCompetitors.map((v, i) => (
                  <P key={i}><b>{v.name}</b> — {v.weakness} <span style={{ color: T.cyanDim }}>→ {v.howToExploit}</span></P>
                ))}
              </Block>
            )}
            {a.websiteInsights.length > 0 && <Block title="Website insights"><Ul items={a.websiteInsights} /></Block>}
            {client.suggestions.length > 0 && (
              <Block title="Client / dev suggestions">
                {client.suggestions.map((s, i) => (
                  <P key={i}><b>{s.title}</b>{s.impact != null ? ` (impact ${s.impact})` : ''} — {s.why}</P>
                ))}
              </Block>
            )}
            {a.optionalNotes.length > 0 && <Block title="Optional notes (not actioned)"><Ul items={a.optionalNotes} /></Block>}
          </>}
        </div>
      )}
    </GBICard>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.fgMuted, marginBottom: 5 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function P({ children, style = {} }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ fontSize: 12.5, color: T.fgSecondary, lineHeight: 1.6, marginBottom: 6, ...style }}>{children}</div>;
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: 12.5, color: T.fgSecondary, lineHeight: 1.6, marginBottom: 4 }}>{t}</li>
      ))}
    </ul>
  );
}

// ── Sticky export bar ───────────────────────────────────────────────────────

function ExportBar({ count, statusNote, includeAnalysis, setIncludeAnalysis, includeActions, setIncludeActions, disabled, onCopy, onMd, onJson, onClear }: {
  count: number;
  statusNote: string;
  includeAnalysis: boolean; setIncludeAnalysis: (v: boolean) => void;
  includeActions: boolean; setIncludeActions: (v: boolean) => void;
  disabled: boolean;
  onCopy: () => void; onMd: () => void; onJson: () => void; onClear: () => void;
}) {
  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
      background: '#fff', borderTop: `1px solid ${T.borderBright}`,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.10)', padding: '12px 24px',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.fgPrimary }}>
          {count} client{count === 1 ? '' : 's'} selected
          {/* The export carries the list's own filters, so say which. */}
          <span style={{ fontWeight: 500, color: T.fgMuted, marginLeft: 6 }}>· {statusNote}</span>
        </span>

        <span style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12, color: T.fgSecondary }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeAnalysis} onChange={e => setIncludeAnalysis(e.target.checked)}
              style={{ accentColor: T.cyan, cursor: 'pointer' }} />
            Data &amp; Analysis
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeActions} onChange={e => setIncludeActions(e.target.checked)}
              style={{ accentColor: T.cyan, cursor: 'pointer' }} />
            Actions
          </label>
        </span>

        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <GBIBtn size="sm" variant="primary" onClick={onCopy} disabled={disabled}>Copy for AI</GBIBtn>
          <GBIBtn size="sm" variant="secondary" onClick={onMd} disabled={disabled}>Download .md</GBIBtn>
          <GBIBtn size="sm" variant="secondary" onClick={onJson} disabled={disabled}>Download .json</GBIBtn>
          <GBIBtn size="sm" variant="ghost" onClick={onClear}>Clear</GBIBtn>
        </div>
      </div>
    </div>
  );
}

// ── bits ────────────────────────────────────────────────────────────────────

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      border: 'none', borderBottom: active ? `2px solid ${T.cyan}` : '2px solid transparent',
      background: 'transparent', color: active ? T.cyanDim : T.fgSecondary, padding: '9px 14px',
      marginBottom: -1, cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 500,
      fontFamily: sharedStyles.fontBody,
    }}>{children}</button>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color: T.fgMuted, background: T.bgOverlay,
      borderRadius: 10, padding: '1px 7px', marginLeft: 6,
    }}>{children}</span>
  );
}
